import { Box, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type { B2BEventType, B2BPublicEventCategory } from 'api/requestTypes';
import { ChangeEvent, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { colors } from 'styles/colors';
import {
  B2B_REGISTRATION_DESCRIPTION_FIELD_NAME,
  B2B_REGISTRATION_EVENT_DATE_FIELD_NAME,
  B2B_REGISTRATION_EVENT_NAME_FIELD_NAME,
  B2B_REGISTRATION_EVENT_TIME_FIELD_NAME,
  B2B_REGISTRATION_EVENT_VENUE_FIELD_NAME,
  b2bRegistrationFieldLabelMessageId,
  b2bRegistrationFieldPlaceholderMessageId,
} from '../b2bRegistrationFormFields';
import {
  B2B_EVENT_TYPE_OPTIONS_ORDER,
  B2B_PUBLIC_EVENT_CATEGORY_OPTIONS_ORDER,
  B2B_REGISTRATION_EVENT_TYPE_FIELD_NAME,
  B2B_REGISTRATION_PUBLIC_CATEGORY_FIELD_NAME,
  b2bEventTypeLabelMessageId,
  b2bPublicEventCategoryLabelMessageId,
} from '../b2bEventChipOptions';
import { B2B_FIELD_ERROR_RESERVED_MIN_H } from '../B2bFieldLayout';
import { B2bOptionalFieldLabel } from '../B2bOptionalFieldLabel';
import { B2bRegistrationSectionHeading } from '../B2bRegistrationSectionHeading/B2bRegistrationSectionHeading';
import { B2bYesNoToggle } from '../B2bYesNoToggle/B2bYesNoToggle';
import { ChipGroup } from '../ChipGroup/ChipGroup';
import { FieldDate } from '../FieldDate/FieldDate';
import { FieldText } from '../FieldText/FieldText';
import { FieldTextarea } from '../FieldTextarea/FieldTextarea';
import { FieldTime } from '../FieldTime/FieldTime';

export type B2bEventDetailsFieldKey =
  | 'eventName'
  | 'eventType'
  | 'publicEventCategory'
  | 'description'
  | 'eventDate'
  | 'eventTime'
  | 'eventVenue'
  | 'isVenueConfirmed';

export type B2bEventDetailsSectionProps = {
  eventName: string;
  onEventNameChange: (value: string) => void;
  eventType: B2BEventType | undefined;
  onEventTypeChange: (value: B2BEventType) => void;
  /** Shown whenever `eventType` is set; included in the API body for all visibility types. */
  publicEventCategory: B2BPublicEventCategory | undefined;
  onPublicEventCategoryChange: (value: B2BPublicEventCategory) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  eventDate: string | undefined;
  onEventDateChange: (value: string | undefined) => void;
  eventTime: string | undefined;
  onEventTimeChange: (value: string | undefined) => void;
  eventVenue: string;
  onEventVenueChange: (value: string) => void;
  isVenueConfirmed: boolean;
  onIsVenueConfirmedChange: (value: boolean) => void;
  errors?: Partial<Record<B2bEventDetailsFieldKey, ReactNode>>;
  isDisabled?: boolean;
  dir?: 'ltr' | 'rtl';
  lang?: string;
};

/**
 * **Event details** — visibility + category (`ChipGroup`), description, schedule, venue, and venue confirmation toggle.
 * Category chips appear once an event visibility type is selected.
 */
export const B2bEventDetailsSection = ({
  eventName,
  onEventNameChange,
  eventType,
  onEventTypeChange,
  publicEventCategory,
  onPublicEventCategoryChange,
  description,
  onDescriptionChange,
  eventDate,
  onEventDateChange,
  eventTime,
  onEventTimeChange,
  eventVenue,
  onEventVenueChange,
  isVenueConfirmed,
  onIsVenueConfirmedChange,
  errors,
  isDisabled,
  dir = 'rtl',
  lang = 'ar',
}: B2bEventDetailsSectionProps) => {
  const intl = useIntl();
  const eventNamePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('eventName'),
    defaultMessage: 'Event title',
  });
  const descriptionPlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('description'),
    defaultMessage: 'Describe the event, audience, and key details…',
  });
  const eventVenuePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('eventVenue'),
    defaultMessage: 'Venue name or area',
  });
  const eventDatePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('eventDate'),
    defaultMessage: 'Select date',
  });
  const eventTimePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('eventTime'),
    defaultMessage: 'Select time',
  });

  const handleEventNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onEventNameChange(e.target.value);
  };
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onDescriptionChange(e.target.value);
  };
  const handleEventVenueChange = (e: ChangeEvent<HTMLInputElement>) => {
    onEventVenueChange(e.target.value);
  };

  return (
    <Box w='100%' dir={dir} lang={lang}>
      <VStack spacing={4} align='stretch' w='100%'>
        <B2bRegistrationSectionHeading dir={dir}>
          <FormattedMessage
            id='b2b.registration.section.eventDetails.title'
            defaultMessage='Event information'
          />
        </B2bRegistrationSectionHeading>

        <FieldText
          id={`b2b-field-${B2B_REGISTRATION_EVENT_NAME_FIELD_NAME}`}
          name={B2B_REGISTRATION_EVENT_NAME_FIELD_NAME}
          label={
            <B2bOptionalFieldLabel>
              <FormattedMessage
                id={b2bRegistrationFieldLabelMessageId('eventName')}
                defaultMessage='Event name'
              />
            </B2bOptionalFieldLabel>
          }
          value={eventName}
          onChange={handleEventNameChange}
          isDisabled={isDisabled}
          error={errors?.eventName}
          placeholder={eventNamePlaceholder}
          layoutDir={dir}
        />

        <Box w='full'>
          <VStack spacing={3} align='stretch' w='full'>
            <ChipGroup<B2BEventType>
              name={B2B_REGISTRATION_EVENT_TYPE_FIELD_NAME}
              selectedVisual='soft'
              groupLabel={
                <FormattedMessage
                  id='b2b.registration.eventType.groupLabel'
                  defaultMessage='Event visibility'
                />
              }
              options={B2B_EVENT_TYPE_OPTIONS_ORDER.map((v) => ({
                value: v,
                label: <FormattedMessage id={b2bEventTypeLabelMessageId(v)} defaultMessage={v} />,
              }))}
              value={eventType}
              onChange={onEventTypeChange}
              isDisabled={isDisabled}
            />
            {eventType != null ? (
              <ChipGroup<B2BPublicEventCategory>
                name={B2B_REGISTRATION_PUBLIC_CATEGORY_FIELD_NAME}
                selectedVisual='solid'
                groupLabel={
                  <FormattedMessage
                    id='b2b.registration.publicCategory.groupLabel'
                    defaultMessage='Event category'
                  />
                }
                options={B2B_PUBLIC_EVENT_CATEGORY_OPTIONS_ORDER.map((v) => ({
                  value: v,
                  label: (
                    <FormattedMessage
                      id={b2bPublicEventCategoryLabelMessageId(v)}
                      defaultMessage={v}
                    />
                  ),
                }))}
                value={publicEventCategory}
                onChange={onPublicEventCategoryChange}
                isDisabled={isDisabled}
              />
            ) : null}
            <Box minH={B2B_FIELD_ERROR_RESERVED_MIN_H} mt={1} w='full'>
              {errors?.eventType != null ? (
                <Text textStyle='bodySmallRegular' color={colors.red} role='alert'>
                  {errors.eventType}
                </Text>
              ) : null}
              {errors?.publicEventCategory != null ? (
                <Text textStyle='bodySmallRegular' color={colors.red} role='alert'>
                  {errors.publicEventCategory}
                </Text>
              ) : null}
            </Box>
          </VStack>
        </Box>

        <FieldTextarea
          id={`b2b-field-${B2B_REGISTRATION_DESCRIPTION_FIELD_NAME}`}
          name={B2B_REGISTRATION_DESCRIPTION_FIELD_NAME}
          label={
            <FormattedMessage
              id={b2bRegistrationFieldLabelMessageId('description')}
              defaultMessage='Event description'
            />
          }
          value={description}
          onChange={handleDescriptionChange}
          isRequired
          isDisabled={isDisabled}
          error={errors?.description}
          placeholder={descriptionPlaceholder}
          layoutDir={dir}
        />

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} w='full' dir={dir}>
          <Box minW={0} w='full'>
            <FieldDate
              id={`b2b-field-${B2B_REGISTRATION_EVENT_DATE_FIELD_NAME}`}
              name={B2B_REGISTRATION_EVENT_DATE_FIELD_NAME}
              label={
                <B2bOptionalFieldLabel>
                  <FormattedMessage
                    id='b2b.registration.field.eventDate.label'
                    defaultMessage='Event date'
                  />
                </B2bOptionalFieldLabel>
              }
              value={eventDate}
              onValueChange={onEventDateChange}
              isOptional
              isDisabled={isDisabled}
              error={errors?.eventDate}
              placeholder={eventDatePlaceholder}
              layoutDir={dir}
            />
          </Box>

          <Box minW={0} w='full'>
            <FieldTime
              id={`b2b-field-${B2B_REGISTRATION_EVENT_TIME_FIELD_NAME}`}
              name={B2B_REGISTRATION_EVENT_TIME_FIELD_NAME}
              label={
                <B2bOptionalFieldLabel>
                  <FormattedMessage
                    id='b2b.registration.field.eventTime.label'
                    defaultMessage='Event time'
                  />
                </B2bOptionalFieldLabel>
              }
              value={eventTime}
              onValueChange={onEventTimeChange}
              isOptional
              isDisabled={isDisabled}
              error={errors?.eventTime}
              placeholder={eventTimePlaceholder}
              layoutDir={dir}
            />
          </Box>
        </SimpleGrid>

        <FieldText
          id={`b2b-field-${B2B_REGISTRATION_EVENT_VENUE_FIELD_NAME}`}
          name={B2B_REGISTRATION_EVENT_VENUE_FIELD_NAME}
          label={
            <B2bOptionalFieldLabel>
              <FormattedMessage
                id='b2b.registration.field.eventVenue.label'
                defaultMessage='Venue'
              />
            </B2bOptionalFieldLabel>
          }
          value={eventVenue}
          onChange={handleEventVenueChange}
          isDisabled={isDisabled}
          error={errors?.eventVenue}
          placeholder={eventVenuePlaceholder}
          layoutDir={dir}
        />

        <B2bYesNoToggle
          groupLabel={
            <FormattedMessage
              id='b2b.registration.field.isVenueConfirmed.label'
              defaultMessage='The venue for this event is confirmed'
            />
          }
          yesLabel={<FormattedMessage id='b2b.registration.yesNo.yes' defaultMessage='Yes' />}
          noLabel={<FormattedMessage id='b2b.registration.yesNo.no' defaultMessage='No' />}
          value={isVenueConfirmed}
          onChange={onIsVenueConfirmedChange}
          isRequired
          isDisabled={isDisabled}
          error={errors?.isVenueConfirmed}
          dir={dir}
        />
      </VStack>
    </Box>
  );
};
